import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../config/api";
import { setUser } from "../utils/auth";
import axiosInstance from "../utils/axios";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Token is now stored in HTTP-only cookie by backend
      // We only need to save user info to localStorage
      const response = await axiosInstance.post(API_ENDPOINTS.LOGIN, {
        email,
        password,
      });

      // Save user info to localStorage (token is in cookie)
      if (response.data.user) {
        setUser(response.data.user);
      }

      // Redirect to dashboard
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Login failed. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative">
      {/* Left Section - Device with Gradient Background */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: "linear-gradient(1deg, #53B1FD 0%, #003CB3 100%)" }}>
        {/* Background Pattern - Circuit Board Pattern */}
        <div className="absolute inset-0 opacity-30">
          <svg className="w-full h-full" viewBox="0 0 1440 960" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Subtle circuit pattern lines */}
            <path d="M200 100 L300 150 L400 100" stroke="rgba(255,255,255,0.2)" strokeWidth="2" fill="none" />
            <circle cx="200" cy="100" r="3" fill="rgba(255,255,255,0.3)" />
            <circle cx="300" cy="150" r="3" fill="rgba(255,255,255,0.3)" />
            <circle cx="400" cy="100" r="3" fill="rgba(255,255,255,0.3)" />
          </svg>
        </div>

        {/* Group Icon - Left Top - Start of Page */}
        <div className="absolute top-0 left-0 z-30">
          <img
            src="/assets/Group.svg"
            alt="UPU Group Logo"
            className="w-auto opacity-30"
            style={{
              height: "600px",
              maxHeight: "70vh",
              filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.2))"
            }}
          />
        </div>

        {/* Main Device Container */}
        <div className="relative z-10 flex flex-col justify-center items-center w-full px-16 py-12">
          {/* Tracker Device */}
          <div className="relative mb-8">
            <img
              src="/assets/tracker.svg"
              alt="UPU Tracker Device"
              className="max-w-full h-auto"
              style={{ maxHeight: "50vh", filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.3))" }}
            />
          </div>

          {/* Bottom Logo - Below Device, Centered */}
          <div className="relative z-20 mt-4">
            <img
              src="/assets/logo_full_white.svg"
              alt="UPU Tracker Logo"
              className="h-12 md:h-14 lg:h-16 w-auto mx-auto"
            />
          </div>
        </div>
      </div>

      {/* Right Section - Login Form */}
      <div className="w-full lg:w-1/2 relative bg-white flex flex-col">
        {/* Line Pattern Top Right */}
        <div className="absolute top-0 right-0 w-96 h-96 opacity-20 pointer-events-none overflow-hidden">
          <svg className="w-full h-full" viewBox="0 0 462 502" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="374.29" y1="105.4" x2="383.83" y2="113.41" stroke="#F2F4F7" strokeWidth="2" />
            <line x1="397.48" y1="120.12" x2="408.27" y2="126.35" stroke="#F2F4F7" strokeWidth="2" />
            <line x1="420.79" y1="135.09" x2="432.49" y2="139.35" stroke="#F2F4F7" strokeWidth="2" />
            <line x1="444.45" y1="149.95" x2="456.72" y2="152.11" stroke="#F2F4F7" strokeWidth="2" />
          </svg>
        </div>

        {/* Line Pattern Bottom Right */}
        <div className="absolute bottom-0 right-0 w-96 h-96 opacity-20 pointer-events-none overflow-hidden">
          <svg className="w-full h-full transform rotate-180" viewBox="0 0 462 502" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="374.29" y1="105.4" x2="383.83" y2="113.41" stroke="#F2F4F7" strokeWidth="2" />
            <line x1="397.48" y1="120.12" x2="408.27" y2="126.35" stroke="#F2F4F7" strokeWidth="2" />
            <line x1="420.79" y1="135.09" x2="432.49" y2="139.35" stroke="#F2F4F7" strokeWidth="2" />
            <line x1="444.45" y1="149.95" x2="456.72" y2="152.11" stroke="#F2F4F7" strokeWidth="2" />
          </svg>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-8 py-12 relative z-10">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-semibold text-gray-900 mb-3" style={{ fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>
                Log in
              </h1>
              <p className="text-base text-gray-600" style={{ fontFamily: "Inter, sans-serif" }}>
                Welcome back! Please enter your details.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5" style={{ fontFamily: "Inter, sans-serif" }}>
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full px-3.5 py-2.5 border rounded-lg shadow-sm focus:outline-none text-base text-gray-600 placeholder-gray-500"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    borderWidth: "1px",
                    borderColor: "#D1D5DB",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => e.target.style.borderColor = "#1570EF"}
                  onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
                />
              </div>

              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5" style={{ fontFamily: "Inter, sans-serif" }}>
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-3.5 py-2.5 border rounded-lg shadow-sm focus:outline-none text-base text-gray-600 placeholder-gray-500"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    borderWidth: "1px",
                    borderColor: "#D1D5DB",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => e.target.style.borderColor = "#1570EF"}
                  onBlur={(e) => e.target.style.borderColor = "#D1D5DB"}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 px-4.5 rounded-lg font-semibold text-base text-white shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: "#1570EF",
                  border: "1px solid #1570EF",
                  fontFamily: "Inter, sans-serif"
                }}
              >
                {isLoading ? "Logging in..." : "Log in"}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-6 text-center">

              <button
                type="button"
                onClick={() => window.open("https://upu.io", "_blank")}
                className="text-sm font-semibold inline hover:underline"
                style={{
                  color: "#107569",
                  fontFamily: "Inter, sans-serif"
                }}
              >
                upu.io
              </button>
            </div>
          </div>
        </div>

        {/* Copyright Footer */}
        <div className="absolute bottom-8 right-8 z-10">
          <p className="text-sm text-gray-600" style={{ fontFamily: "Inter, sans-serif" }}>
            © upu.io 2024
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
